import { usePage } from "@inertiajs/react"

import type { PickupContext } from "./types"

/**
 * Whether the restaurant behind a pickup screen is serving right now.
 *
 * Read from the shared `pickup` prop rather than threaded down, because the
 * three controls that have to respect it sit at three different depths — the
 * menu's quick-add, the dish drawer's add, and the cart's pay — and passing a
 * boolean through every layer between them is how one of them gets missed.
 *
 * `closed` is false on a dine-in screen, where there is no `pickup` prop at all.
 * That is deliberate and not an oversight: a seated diner was let in by staff who
 * are standing there, so business hours have nothing to say about their table.
 *
 * None of this is the gate. `User::PickupCheckoutService` refuses a closed
 * restaurant on its own, because a tab left open since lunch still holds a live
 * pay button and the cart lives in localStorage where the client owns it. This
 * only stops the app offering something it knows will be refused.
 */
export function usePickupHours() {
  const pickup = usePage<{ pickup?: PickupContext | null }>().props.pickup

  return {
    closed: Boolean(pickup) && pickup?.openNow === false,
    opensAt: pickup?.opensAt ?? null,
    // Null, never "". An empty string is falsy, so `formatDateTime` would drop the
    // `timeZone` option and render in the browser's zone instead — silently, and
    // it is the one thing the frontend date rule forbids. Null makes a caller
    // outside a pickup screen deal with it.
    timeZone: pickup?.timeZone ?? null,
  }
}
